/**
 * @spec docs/specs/features/staking-position-ui.md#design-system
 * @figma https://www.figma.com/design/Z2CmEcX1brcgv3hfhOWO07/Phenom-Poker?node-id=10339-18117
 *
 * Button component matching Phenom Poker design system.
 *
 * Variants:
 * - primary (purple): the single most important action in a view
 * - secondary / grey: tonal surface button for alternative actions
 * - outline (purple-outlined): brand-outlined tertiary action
 * - ghost (link-purple): text-only brand action
 * - grey-outlined: hairline outline, neutral
 * - link-grey: text-only neutral action
 * - white: inverted, for dark-on-light emphasis
 * - tether / tether-outline / tether-ghost: USDT/USDC money-out actions
 *
 * Sizes: sm (32px), md (40px), lg (48px, the default for form submits)
 */
import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'ghost'
  | 'grey'
  | 'grey-outlined'
  | 'link-grey'
  | 'white'
  | 'tether'
  | 'tether-outline'
  | 'tether-ghost';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Visual style variant
   * @default 'primary'
   */
  variant?: ButtonVariant;

  /**
   * Button size
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Show loading spinner and disable interaction
   */
  isLoading?: boolean;

  /**
   * Icon to display before the button text
   */
  leftIcon?: ReactNode;

  /**
   * Icon to display after the button text
   */
  rightIcon?: ReactNode;
}

const TONAL = cn(
  'bg-white/[0.06] text-text-primary border border-white/[0.08]',
  'hover:bg-white/[0.1] hover:border-white/[0.14]',
  'disabled:bg-white/[0.03] disabled:text-text-disabled disabled:border-white/[0.05]'
);

const VARIANTS: Record<ButtonVariant, string> = {
  primary: cn(
    'text-white bg-[linear-gradient(180deg,#ae5ddc_0%,#9a3dcd_100%)] shadow-btn-primary',
    'hover:bg-[linear-gradient(180deg,#b96ce2_0%,#a446d4_100%)]',
    'disabled:bg-none disabled:bg-white/[0.06] disabled:text-text-disabled disabled:shadow-none'
  ),
  secondary: TONAL,
  grey: TONAL,
  outline: cn(
    'border border-brand-400/50 text-brand-200 bg-transparent',
    'hover:bg-brand-500/10 hover:border-brand-400',
    'disabled:border-white/[0.08] disabled:text-text-disabled disabled:bg-transparent'
  ),
  ghost: cn(
    'text-brand-300 bg-transparent',
    'hover:text-brand-200 hover:bg-white/[0.04]',
    'disabled:text-text-disabled disabled:bg-transparent'
  ),
  'grey-outlined': cn(
    'border border-white/[0.1] text-text-secondary bg-transparent',
    'hover:border-white/[0.2] hover:text-text-primary',
    'disabled:border-white/[0.06] disabled:text-text-disabled'
  ),
  'link-grey': cn(
    'text-text-secondary bg-transparent',
    'hover:text-text-primary hover:bg-white/[0.04]',
    'disabled:text-text-disabled disabled:bg-transparent'
  ),
  white: cn(
    'bg-white text-bg-primary',
    'hover:bg-white/90',
    'disabled:bg-white/30 disabled:text-bg-primary/50'
  ),
  tether: cn(
    'text-white bg-[linear-gradient(180deg,#14b0a4_0%,#0a968f_100%)]',
    'shadow-[inset_0_1px_0_0_rgba(255,255,255,0.2),0_8px_24px_-10px_rgba(20,176,164,0.7)]',
    'hover:bg-[linear-gradient(180deg,#1cc2b5_0%,#0ea69e_100%)]',
    'disabled:bg-none disabled:bg-white/[0.06] disabled:text-text-disabled disabled:shadow-none'
  ),
  'tether-outline': cn(
    'border border-accent-tether/50 text-accent-tether bg-transparent',
    'hover:bg-accent-tether/10 hover:border-accent-tether',
    'disabled:border-white/[0.08] disabled:text-text-disabled'
  ),
  'tether-ghost': cn(
    'text-accent-tether bg-transparent',
    'hover:bg-accent-tether/10',
    'disabled:text-text-disabled disabled:bg-transparent'
  ),
};

const SIZES = {
  sm: 'h-8 px-3 gap-1.5 text-[13px] rounded-[10px]',
  md: 'h-10 px-4 gap-2 text-sm rounded-control',
  lg: 'h-12 px-5 gap-2 text-[15px] rounded-control',
} as const;

const ICON_SIZES = {
  sm: 'size-4',
  md: 'size-4',
  lg: 'size-5',
} as const;

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      isLoading,
      disabled,
      leftIcon,
      rightIcon,
      children,
      ...props
    },
    ref
  ) => (
    <button
      ref={ref}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      data-variant={variant}
      data-size={size}
      className={cn(
        'relative inline-flex items-center justify-center whitespace-nowrap',
        'font-bold leading-5 tracking-[-0.005em]',
        'transition-[background-color,border-color,color,box-shadow,transform] duration-150 ease-out',
        'active:scale-[0.98] disabled:active:scale-100',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-primary',
        'cursor-pointer select-none disabled:cursor-not-allowed',
        VARIANTS[variant],
        SIZES[size],
        isLoading && 'text-transparent',
        className
      )}
      {...props}
    >
      {isLoading && (
        <span className="absolute inset-0 flex items-center justify-center text-white">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        </span>
      )}

      {leftIcon && !isLoading && (
        <span className={cn('shrink-0 inline-flex', ICON_SIZES[size])}>{leftIcon}</span>
      )}

      {children}

      {rightIcon && !isLoading && (
        <span className={cn('shrink-0 inline-flex', ICON_SIZES[size])}>{rightIcon}</span>
      )}
    </button>
  )
);

Button.displayName = 'Button';

export { Button };
